import { Cart, CartImpl } from '@/modules/store/models/Cart';

interface SelectableCart {
  supplierId: string;
  items: {
    id: number;
    checked?: boolean;
  }[];
}

class CartSelectionService {
  /**
   * Get the IDs of items that are checked for a specific supplier.
   */
  getSelectedItems(carts: SelectableCart[], supplierId: string): number[] {
    return carts
      .filter((cart) => cart.supplierId === supplierId)
      .flatMap((cart) => cart.items.filter((item) => item.checked).map((item) => item.id));
  }

  /**
   * Updates the checked state of items for a specific supplier.
   *
   * `items` is an array of item IDs that should be checked.
   */
  updateSelectedItems(carts: Cart[], supplierId: string, items: number[]): Cart[] {
    return carts.map((cart) => {
      if (cart.supplierId !== supplierId) {
        return cart;
      }

      const updatedItems = cart.items.map((item) => ({
        ...item,
        checked: items.includes(item.id),
      }));

      return {
        ...cart,
        items: updatedItems,
      };
    });
  }

  /**
   * Returns ONLY the carts that have at least one checked item,
   * and each cart contains only its checked items.
   */
  getSelectedCartsAndItems(carts: Cart[]): Cart[] {
    return carts
      .filter((cart) => cart.items.some((item) => item.checked))
      .map((cart) =>
        CartImpl.fromObject(cart).withItems(cart.items.filter((item) => item.checked)),
      );
  }

  /**
   * Removes all checked items from all carts.
   * Carts with no remaining items are filtered out.
   */
  removeSelectedCartsAndItems(carts: Cart[]): Cart[] {
    const updated = carts.map((cart) => {
      const remainingItems = cart.items.filter((item) => !item.checked);
      return {
        ...cart,
        items: remainingItems,
      };
    });

    return updated.filter((cart) => cart.items.length > 0);
  }
}

export default CartSelectionService;
